import type { CoachAction, CoachProgress } from '../api/client'
import type { Forecast, GoalNumbers, Stretch } from './forecast'
import { formatPrice, minutesToTime } from '../catalog/format'

// Every sentence the Goals screen shows lives here, so the tone can be read, and
// tested, in one place: optimistic, specific, never "impossible".

// formatPounds is formatPrice rounded to the pound, for projections only
// (a forecast, a goal's assumption), where pence would claim a precision the
// guess doesn't have.
export function formatPounds(pence: number): string {
  return formatPrice(Math.round(pence / 100) * 100)
}

const oneDecimal = (n: number) => (Math.round(n * 10) / 10).toLocaleString('en-GB')
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

// DEMAND_LIMIT says under the week's result, not only in its working, why
// more hours leave the take-home where it is.
export const DEMAND_LIMIT = 'Your clients are the limit, not your days: more hours won’t add to this until more clients book.'

export function limitLine(f: Forecast): string {
  if (f.limit === 'no_hours') {
    return f.hours_per_week > 0 ? 'No treatment on your menu fits these hours yet.' : 'Add some hours to see your plan.'
  }
  if (!f.demand_known) {
    return 'This is the most this week can hold. Harmovi will know your demand once more of your history is here.'
  }
  if (f.limit === 'demand') return 'Your clients are the limit, not your days.'
  return 'Your hours are the limit: this week is full.'
}

// workingLine shows how the month was reached, so the number can be checked.
export function workingLine(f: Forecast, costsPence: number): string {
  if (f.limit === 'no_hours') return ''
  const base =
    f.limit === 'demand'
      ? `${oneDecimal(f.visits)} appointments a month from your clients × ${formatPounds(f.avg_value_pence)}`
      : `${oneDecimal(f.weekly_capacity)} treatments a week × ${oneDecimal(f.weeks_per_month)} weeks × ${formatPounds(f.avg_value_pence)}`
  return costsPence > 0 ? `${base} − ${formatPounds(costsPence)} costs` : base
}

export function gapLine(f: Forecast, targetPence: number): string {
  if (f.limit === 'no_hours') return ''
  if (f.gap_pence <= 0) return `That reaches your ${formatPounds(targetPence)} take-home goal.`
  const base = `${formatPounds(f.gap_pence)} to go to take home ${formatPounds(targetPence)}`
  return leverLines(f).length > 0 ? `${base} — here’s what would close it:` : `${base}.`
}

// leverLines are the ways to close a gap, and only those that would.
export function leverLines(f: Forecast): string[] {
  if (f.gap_pence <= 0) return []
  const l = f.levers
  const lines: string[] = []
  if (l.price_rise_percent !== null) lines.push(`Raise your average price by ${l.price_rise_percent}%.`)
  if (l.more_clients !== null && l.add_half_days !== null) {
    lines.push(`Find ${plural(l.more_clients, 'more regular client')} and add ${plural(l.add_half_days, 'half-day')} a week.`)
  } else if (l.more_clients !== null) {
    lines.push(`Find ${plural(l.more_clients, 'more regular client')}.`)
  } else if (l.add_half_days !== null) {
    lines.push(`Add ${plural(l.add_half_days, 'half-day')} a week.`)
  }
  if (l.hours_wont_help && l.add_half_days === null) lines.push("More hours won’t help yet: your diary isn’t full.")
  return lines
}

// Jessica thinks in months, not weeks (feedback 2026-10-11): the week is what
// she paints, but every summary leads with what it adds up to in a month.

// hoursPerMonth is the week's hours across an average working month, holidays
// taken out.
export function hoursPerMonth(f: Forecast): number {
  return Math.round(f.hours_per_week * f.weeks_per_month)
}

export function actionWorth(a: CoachAction): string {
  if (a.hours_freed) return `About ${a.hours_freed} hours a week back`
  return a.impact_pence > 0 ? `Worth about ${formatPounds(a.impact_pence)}` : ''
}

// ---- The one plan screen (goal, week and month together) ----

export const MONTH_NOT_LIVE = 'Once your bookings are in Harmovi, you’ll see how each month is really going here too.'
export const MONTH_NEEDS_PLAN = 'Save your plan to see how each month is going.'
export const TAP_A_DAY = 'Tap a day to change it'
export const TAP_DAYS_TO_START = 'Tap the days you’d like to work'

// The ⓘ beside "Dream Week" and the bubble it opens.
export const DREAM_WEEK_ABOUT_TITLE = 'What’s your Dream Week?'
export const DREAM_WEEK_ABOUT = [
  'The week you’d like to work, and what it would take home a month.',
  'It stays a plan until you let clients book these hours.',
]

// Starting the week from her working hours: a link beside the week, and the
// question it asks first when she has already filled in some days.
export const COPY_WORKING_HOURS = 'Copy in my working hours'
export const COPY_WORKING_HOURS_ASK = 'Replace your Dream Week with your working hours?'
export const COPY_WORKING_HOURS_KEEP = 'Keep mine'
export const COPY_WORKING_HOURS_REPLACE = 'Replace it'

// The Dream Week's footer: the quiet save, the one filled button, and what
// the footer says beside them.
export const SAVE_AS_PLAN = 'Save as a plan'
export const LET_CLIENTS_BOOK = 'Let clients book these hours'
export const FOOTER_NOT_SAVED = 'Not saved'
export const FOOTER_PLAN_ONLY = 'Clients can’t book these yet'
export const FOOTER_UNDO = 'Undo'
export const DREAM_WEEK_SAVED = 'Dream Week saved'
export const CLIENTS_CAN_BOOK = 'Clients can now book these hours'

// The freedom suggestion under a week that's over its goal.
export const DROP_DAY_TRY = 'Try it'
export function dropDayLine(dayName: string, takeHomePence: number): string {
  return `Drop ${dayName} and you’d still take home ${formatPounds(takeHomePence)} a month.`
}

// Letting clients book asks first, listing each day that changes.
export const GO_LIVE_ASK = 'Let clients book these hours?'
export const GO_LIVE_LEAD = 'Clients will be able to book you at these times:'
export const GO_LIVE_NOT_YET = 'Not yet'
export const GO_LIVE_YES = 'Yes, let clients book'

// goLiveWas is what a changed day is now, beside what it will be.
export function goLiveWas(stretches: Stretch[]): string {
  if (stretches.length === 0) return 'now: not working'
  return `now: ${stretches.map((s) => `${minutesToTime(s.start_minute)} to ${minutesToTime(s.end_minute)}`).join(' and ')}`
}
export const FIRST_TIME_HEADING = 'Let’s start with what you want'
export const NO_MENU_MESSAGE = 'Add a treatment to your menu first. Your plan is built from your prices and lengths.'

// weekStatus is the Dream Week's tag: a change waiting to be saved, her
// working hours (what clients can book), or a plan only. No week, no tag.
export function weekStatus(unsaved: boolean, live: boolean, saved: boolean): { text: string; tone: 'warning' | 'positive' | 'neutral' } | null {
  if (unsaved) return { text: 'Not saved', tone: 'warning' }
  if (!saved) return null
  return live ? { text: 'Your working hours', tone: 'positive' } : { text: 'Plan only', tone: 'neutral' }
}

// goalHeadline is the goal itself, in months: the label above it says it's
// the goal, and goalSubline says what the number means.
export function goalHeadline(goal: GoalNumbers): string {
  return `${formatPounds(goal.take_home_target_pence)} a month`
}

export function goalSubline(goal: GoalNumbers): string {
  const takeHome = goal.monthly_costs_pence > 0 ? `to take home, after ${formatPounds(goal.monthly_costs_pence)} costs` : 'to take home'
  const weeks = goal.holiday_weeks_per_year
  return `${takeHome} · ${weeks > 0 ? `${plural(weeks, 'week')} off a year` : 'no time off planned'}`
}

// goalScaleLabel marks the right end of each bar, so the bar can be read
// against the goal.
export function goalScaleLabel(targetPence: number): string {
  return `Goal ${formatPounds(targetPence)}`
}

// weekSummary is the Dream Week header's one line: its shape and what it
// would take home a month.
export function weekSummary(f: Forecast): string {
  return `${weekShape(f)} · ${formatPounds(Math.max(0, f.take_home_pence))} a month`
}

export function weekShape(f: Forecast): string {
  return `${plural(f.working_days, 'day')} · ${oneDecimal(f.hours_per_week)} h`
}

export function weekBarLine(f: Forecast): { text: string; reached: boolean } {
  if (f.limit === 'no_hours') {
    return { text: f.hours_per_week > 0 ? 'No treatment on your menu fits these hours yet.' : TAP_DAYS_TO_START, reached: false }
  }
  if (f.gap_pence <= 0) return { text: 'Reaches your goal', reached: true }
  return { text: `${formatPounds(f.gap_pence)} short of your goal`, reached: false }
}

export const WEEK_LEAD = 'Every week like this would take home'

// weekGapNote sits beside the week's amount. With no hours there is no gap
// to name: weekBarLine says what to do instead, under the bar.
// Over the goal it says by how much, so a week with room to spare reads as
// room to spare.
export function weekGapNote(f: Forecast, targetPence: number): { text: string; reached: boolean } {
  if (f.limit === 'no_hours') return { text: '', reached: false }
  const over = f.take_home_pence - targetPence
  if (f.gap_pence <= 0) return { text: over >= 100 ? `${formatPounds(over)} over your goal` : 'Reaches your goal', reached: true }
  return { text: `${formatPounds(f.gap_pence)} short`, reached: false }
}

export interface MonthTakeHome {
  soFarPence: number
  onPacePence: number
  takingsPence: number
  costsCovered: boolean
}

// monthTakeHome turns the month's takings into take-home: costs come off in
// full, and never below nothing.
export function monthTakeHome(p: CoachProgress, costsPence: number): MonthTakeHome {
  return {
    soFarPence: Math.max(0, p.so_far_pence - costsPence),
    onPacePence: Math.max(0, p.projected_pence - costsPence),
    takingsPence: p.so_far_pence,
    costsCovered: p.so_far_pence >= costsPence,
  }
}

const monthName = (now: Date) => now.toLocaleDateString('en-GB', { month: 'long' })

export function monthSoFarLabel(now: Date): string {
  return `${monthName(now)} so far`
}

export function monthLead(costsPence: number): string {
  return costsPence > 0 ? 'After this month’s costs, you’ve taken home' : 'This month you’ve taken home'
}

// monthPaceNote sits beside the month's amount, but only once costs are
// covered: before then the pace would be take-home she hasn't got yet.
export function monthPaceNote(m: MonthTakeHome): string {
  return m.costsCovered ? `on pace for ${formatPounds(m.onPacePence)}` : ''
}

// monthCostsLine is what the month shows instead, under its bar.
export function monthCostsLine(m: MonthTakeHome, costsPence: number): string {
  return m.costsCovered ? '' : `${formatPounds(m.takingsPence)} of ${formatPounds(costsPence)} costs covered so far`
}

export function monthTickLine(now: Date): string {
  return `The tick is where ${monthName(now)} will land at this pace.`
}

export function dayLabel(dayName: string, stretches: Stretch[]): string {
  if (stretches.length === 0) return `${dayName}, not working`
  return `${dayName}, ${stretches.map((s) => `${minutesToTime(s.start_minute)} to ${minutesToTime(s.end_minute)}`).join(' and ')}`
}

// The Freedom Score row.
export const FREEDOM_STARTS = 'Starts after 4 weeks'
export const FREEDOM_GOING_WELL = 'Going well'
export const FREEDOM_WORK_ON = 'Work on'
export const FREEDOM_HOW_SCORED = 'How it’s scored'

// The ⓘ beside the row and the bubble it opens: what the score is, before
// she's opened anything.
export const FREEDOM_ABOUT_TITLE = 'What’s the Freedom Score?'
export const FREEDOM_ABOUT = [
  'How freely your business runs, out of 100. Not just how much you take, but how well it fits the life you want.',
  'Five parts count equally: being on track for your goal, steady income, working within your Dream Week, clients coming back, and keeping what you earn.',
  'It looks at your last full week and updates once a week.',
]

// freedomScoredOn says, beside the score, that it rests on only the parts
// measured so far while some still need more history.
export function freedomScoredOn(measured: number): string {
  return `Early score · ${measured} of 5 parts`
}

// freedomPoints shows a part's points as a whole number: 15.6 reads as 16.
export function freedomPoints(points: number): string {
  return String(Math.round(points))
}

// freedomPartPeriod says what stretch of her business a Freedom Score part
// looks at, so its points can be read against something.
export function freedomPartPeriod(key: string): string {
  switch (key) {
    case 'on_track':
      return 'This month, against your goal'
    case 'steady_income':
      return 'Your takings, last 12 weeks'
    case 'time_for_you':
      return 'Your hours, last 4 weeks, against your Dream Week'
    case 'coming_back':
      return 'Your regular clients now'
    case 'keeping_earned':
      return 'Profit, last 3 months'
    default:
      return ''
  }
}
